import React from 'react';
import { useNavigate } from 'react-router';
import { ExternalLink } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { orgPath } from '@/utils/org-path';

/**
 * MCP tab content for a workflow published through the MCP gateway: the
 * live endpoint, the copyable mcp.json connection snippet returned by the
 * publish response, and a deep link to the MCP Servers admin page (keys
 * are minted there — never shown in the publish flow). The legacy sample
 * snippet keeps the plain CodeTabContent rendering; this component only
 * mounts when the publish response carried a real endpoint.
 */
export const McpPublishTabContent: React.FC<{
  mcpCode: string;
  mcpEndpoint: string;
}> = ({ mcpCode, mcpEndpoint }) => {
  const navigate = useNavigate();

  return (
    <TabsContent
      value='mcp'
      className='flex-1 flex flex-col gap-2 mt-0 min-h-0 p-2 h-0'
    >
      <div className='rounded-md border p-3 space-y-2'>
        <p className='text-sm font-medium'>Live MCP endpoint</p>
        <p className='text-xs text-muted-foreground font-mono break-all'>
          {mcpEndpoint}
        </p>
        <p className='text-xs text-muted-foreground'>
          Add the snippet below to your MCP client (Cursor:{' '}
          <code>~/.cursor/mcp.json</code>; Claude Desktop:{' '}
          <code>claude_desktop_config.json</code>), then mint an API key from
          the server&apos;s Credentials tab.
        </p>
        <Button
          variant='outline'
          size='sm'
          onClick={() => navigate(orgPath('/settings/mcp-servers'))}
        >
          <ExternalLink className='w-3.5 h-3.5 mr-1.5' />
          Manage MCP servers
        </Button>
      </div>
      <div className='flex-1 min-h-0 relative'>
        <div className='absolute top-2 right-4 z-10'>
          <CopyButton text={mcpCode} size='default' />
        </div>
        <div className='h-full overflow-y-auto bg-zinc-100 dark:bg-zinc-800 p-4 rounded-md'>
          <SyntaxHighlighter code={mcpCode} language='json' />
        </div>
      </div>
    </TabsContent>
  );
};
